import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { DatePicker, Select, Input } from "antd";
import {
    GetDocumentosAnulados,
    GetDocumentosAnuladosExcel,
    GetDocumentosAnuladosPdf,
} from "../../Service/Service.reporteAnulados";
import DataTable from "../../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble } from "../../Components/UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../Components/UI/Chip";
import BotonKit from "../../Components/UI/Boton";
import BotonesExportar from "../../Components/UI/BotonesExportar";
import { useToast } from "../../Components/UI/Aviso";
import { IconoBuscar } from "../../Components/UI/Iconos";
import { usePageSubtitulo } from "../../context/PageHeaderContext";

const { RangePicker } = DatePicker;

// ANULACION-FISCAL-64 -- Reporte de Documentos Anulados.
//
// Vista ANALÍTICA y 100% solo lectura: no anula, no re-emite, no reactiva.
// Grano = 1 documento anulado, de cualquier tipo y cualquier serie.
//
// Lo que este reporte existe para responder, y que antes no se podía saber sin
// mirar la BD: qué correlativos se anularon, por qué, cuándo, quién, y sobre
// todo si el número volvió a la secuencia o quedó quemado. Ese último dato es
// el que hace cuadrar la numeración frente al SRI.
//
// Filtros SIEMPRE VISIBLES (misma regla UX de 06/07/09).

const CAMPO_FECHA = [
    { value: "ANULACION", label: "Fecha de anulación" },
    { value: "EMISION", label: "Fecha de emisión" },
];

const FISCAL = [
    { value: "SI", label: "Sí — emitida al SRI" },
    { value: "NO", label: "No — nunca salió" },
];

const SECUENCIAL = [
    { value: "LIBERADO", label: "Liberado (reutilizable)" },
    { value: "QUEMADO", label: "Quemado" },
    { value: "DESCONOCIDO", label: "No se sabe" },
];

// El color significa lo mismo que en el resto del sistema (ver Chip.js).
// "Quemado" no es un error ni una alerta: es el resultado CORRECTO de anular
// un documento fiscal, así que va en gris/pendiente, no en rojo. El rojo se
// reserva para lo que sí necesita atención.
const TONO_SECUENCIAL = { LIBERADO: "hecho", QUEMADO: "pendiente", DESCONOCIDO: "proceso" };
const LABEL_SECUENCIAL = {
    LIBERADO: "Liberado",
    QUEMADO: "Quemado",
    DESCONOCIDO: "No se sabe",
};
const AYUDA_SECUENCIAL = {
    LIBERADO: "El número volvió a la secuencia: la siguiente factura lo reutiliza.",
    QUEMADO: "El número no se reutiliza. Queda como hueco en la numeración.",
    DESCONOCIDO: "Anulada antes de que el sistema registrara esta decisión.",
};

const EMPTY = {
    rangoFechas: null,
    campo_fecha: "ANULACION",
    documento: undefined,
    num_documento: undefined,
    fiscal: undefined,
    secuencial: undefined,
};

// Un solo sitio para volcar el blob que devuelve el backend.
const descargarBlob = (respuesta, nombre) => {
    const url = URL.createObjectURL(respuesta.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
};

function ReporteAnulados() {
    const [draft, setDraft] = useState(EMPTY);
    const [applied, setApplied] = useState({ campo_fecha: "ANULACION" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState({ documentos: 0, importe_anulado: 0, fiscales: 0, liberados: 0, quemados: 0 });
    const { mostrar } = useToast();

    const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

    const fetchDatos = useCallback(async (p = 1, ps = 20, filters = {}) => {
        setLoading(true);
        setError(null);
        const res = await GetDocumentosAnulados({ page: p, limit: ps, filters });
        if (res?.success) {
            setRows((res.data || []).map((r) => ({ ...r, key: r.idfactura })));
            setTotal(res.pagination?.total || 0);
            setPage(res.pagination?.page || 1);
            setPageSize(res.pagination?.limit || ps);
            setSummary(res.summary || { documentos: 0, importe_anulado: 0, fiscales: 0, liberados: 0, quemados: 0 });
        } else {
            // ESTADO DE ERROR explícito: una tabla vacía en silencio sería
            // indistinguible de "no se anuló nada", que aquí es justo la
            // conclusión peligrosa.
            setRows([]);
            setTotal(0);
            setError(res?.message || "No se pudo obtener el reporte de documentos anulados.");
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchDatos(1, 20, { campo_fecha: "ANULACION" });
    }, [fetchDatos]);

    const aplicar = () => {
        const [d0, d1] = draft.rangoFechas || [];
        const f = {
            fecha_inicial: d0 ? d0.format("YYYY-MM-DD") : undefined,
            fecha_final: d1 ? d1.format("YYYY-MM-DD") : undefined,
            campo_fecha: draft.campo_fecha,
            documento: draft.documento,
            num_documento: draft.num_documento,
            fiscal: draft.fiscal,
            secuencial: draft.secuencial,
        };
        setApplied(f);
        fetchDatos(1, pageSize, f);
    };

    const limpiar = () => {
        setDraft(EMPTY);
        const f = { campo_fecha: "ANULACION" };
        setApplied(f);
        fetchDatos(1, pageSize, f);
    };

    const columnas = [
        {
            key: "documento",
            titulo: "Documento",
            ancho: 200,
            render: (r) => (
                <CeldaDoble
                    principal={r.num_documento || "—"}
                    secundario={`${r.documento || "—"} · ${r.fecha_emision || "—"}`}
                    monoPrincipal
                />
            ),
        },
        {
            key: "receptor",
            titulo: "Receptor",
            render: (r) => <Celda valor={r.receptor || "Sin determinar"} />,
        },
        {
            key: "total",
            titulo: "Importe",
            ancho: 120,
            alinear: "derecha",
            render: (r) => <Celda valor={money(r.total)} mono />,
        },
        {
            key: "anulacion",
            titulo: "Anulación",
            ancho: 190,
            render: (r) => (
                <CeldaDoble
                    principal={r.fecha_anulacion || "Sin registrar"}
                    secundario={r.anulado_por || "—"}
                />
            ),
        },
        {
            key: "motivo",
            titulo: "Motivo",
            render: (r) => <Celda valor={r.motivo || "—"} />,
        },
        {
            key: "fiscal",
            titulo: "Emitida al SRI",
            ancho: 130,
            render: (r) =>
                r.fiscal ? (
                    <ChipCategoria tono="info" title={r.autorizacion ? `Autorización: ${r.autorizacion}` : "Documento creado en Contífico"}>
                        Sí
                    </ChipCategoria>
                ) : (
                    <ChipCategoria tono="neutro" title="Nunca se envió a Contífico/SRI">
                        No
                    </ChipCategoria>
                ),
        },
        {
            key: "secuencial",
            titulo: "Secuencial",
            ancho: 150,
            render: (r) => (
                <ChipEstado tono={TONO_SECUENCIAL[r.secuencial] || "pendiente"} title={AYUDA_SECUENCIAL[r.secuencial]}>
                    {LABEL_SECUENCIAL[r.secuencial] || r.secuencial}
                </ChipEstado>
            ),
        },
    ];

    const hayFiltros = Object.entries(applied).some(
        ([k, v]) => k !== "campo_fecha" && v !== undefined && v !== null && v !== ""
    );

    usePageSubtitulo(loading ? "" : `${total} documento${total === 1 ? "" : "s"} · ${money(summary.importe_anulado)} anulado`);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Row className="mb-2">
                    <Col>
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item">Reportes</li>
                            <li className="breadcrumb-item active">Documentos Anulados</li>
                        </ol>
                    </Col>
                </Row>

                {/* ---- FILTROS SIEMPRE VISIBLES ---- */}
                <Row className="g-2 align-items-end mb-3 p-2 border rounded bg-light">
                    <Col xs={12} md={4} lg={3}>
                        <label className="kc__etiqueta"><small>Rango de fechas</small></label>
                        <RangePicker
                            className="w-100"
                            value={draft.rangoFechas}
                            onChange={(d) => setDraft((f) => ({ ...f, rangoFechas: d }))}
                            getPopupContainer={() => document.body}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        {/* Sin esto el rango sería ambiguo: "anuladas en agosto"
                            y "facturas de agosto que se anularon" son preguntas
                            distintas y las dos se hacen en la práctica. */}
                        <label className="kc__etiqueta"><small>El rango aplica a</small></label>
                        <Select
                            className="w-100"
                            options={CAMPO_FECHA}
                            value={draft.campo_fecha}
                            onChange={(v) => setDraft((f) => ({ ...f, campo_fecha: v }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>N° de documento</small></label>
                        <Input
                            className="w-100"
                            allowClear
                            placeholder="Buscar..."
                            value={draft.num_documento || ""}
                            onChange={(e) => setDraft((f) => ({ ...f, num_documento: e.target.value || undefined }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Emitida al SRI</small></label>
                        <Select
                            className="w-100"
                            allowClear
                            placeholder="Todas"
                            options={FISCAL}
                            value={draft.fiscal}
                            onChange={(v) => setDraft((f) => ({ ...f, fiscal: v }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Secuencial</small></label>
                        <Select
                            className="w-100"
                            allowClear
                            placeholder="Todos"
                            options={SECUENCIAL}
                            value={draft.secuencial}
                            onChange={(v) => setDraft((f) => ({ ...f, secuencial: v }))}
                        />
                    </Col>
                    <Col xs={12} lg={12} className="d-flex gap-2 mt-2 flex-wrap">
                        <BotonKit variante="primario" icono={IconoBuscar} onClick={aplicar} data-testid="anulados-buscar">
                            Buscar
                        </BotonKit>
                        <BotonKit onClick={limpiar} data-testid="anulados-limpiar">
                            Limpiar
                        </BotonKit>
                        {/* Ambos archivos los genera el backend; el permiso es
                            barrera real en esas rutas. */}
                        <BotonesExportar
                            permiso="reportes.export"
                            titulo="Documentos Anulados"
                            idPrefijo="anulados"
                            onExcel={async () => {
                                try {
                                    const r = await GetDocumentosAnuladosExcel({ filters: applied });
                                    descargarBlob(r, "DOCUMENTOS_ANULADOS.xlsx");
                                } catch (e) {
                                    mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el Excel." });
                                }
                            }}
                            onPdf={async () => {
                                try {
                                    const r = await GetDocumentosAnuladosPdf({ filters: applied });
                                    descargarBlob(r, "DOCUMENTOS_ANULADOS.pdf");
                                } catch (e) {
                                    mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el PDF." });
                                }
                            }}
                        />
                    </Col>
                </Row>

                <div className="ac-filtros-linea mb-3" data-testid="anulados-totales">
                    {[
                        { etiqueta: "Documentos anulados", valor: summary.documentos },
                        { etiqueta: "Importe anulado", valor: money(summary.importe_anulado) },
                        { etiqueta: "Emitidos al SRI", valor: summary.fiscales },
                        { etiqueta: "Secuencial liberado", valor: summary.liberados },
                        { etiqueta: "Secuencial quemado", valor: summary.quemados },
                    ].map((t) => (
                        <div className="ac-panel" key={t.etiqueta} style={{ flex: "1 1 160px", padding: "10px 14px", gap: 2 }}>
                            <span className="ac-etiqueta">{t.etiqueta}</span>
                            <span style={{ fontSize: 18, fontWeight: 600 }}>{t.valor}</span>
                        </div>
                    ))}
                </div>

                <DataTable
                    columnas={columnas}
                    filas={rows}
                    filaKey="idfactura"
                    cargando={loading}
                    error={error}
                    onReintentar={() => fetchDatos(page, pageSize, applied)}
                    etiquetaTabla="Documentos anulados"
                    hayFiltrosAplicados={hayFiltros}
                    onLimpiarFiltros={limpiar}
                    textoVacioSinFiltros="No se ha anulado ningún documento todavía."
                    paginacion={{
                        page,
                        pageSize,
                        total,
                        tamanos: [20, 50, 100],
                        onCambio: (p, ps) => fetchDatos(p, ps, applied),
                    }}
                />
            </div>
        </div>
    );
}

export default ReporteAnulados;
